import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Warehouse, 
  Truck, 
  ShieldCheck, 
  FileText,
  ArrowRight,
  Package,
  Globe,
  HeadphonesIcon
} from 'lucide-react';

export const ServicesPage = () => {
  const { t, isRTL } = useLanguage();

  const mainServices = [
    { 
      icon: Warehouse, 
      titleKey: 'services.service1Title', 
      descKey: 'services.service1Desc',
      details: [
        'Access to verified German warehouses',
        'Product sourcing and procurement',
        'Price negotiation on your behalf',
        'Quality verification before purchase'
      ],
      detailsAr: [
        'الوصول إلى المستودعات الألمانية المعتمدة',
        'توفير المنتجات والمشتريات',
        'التفاوض على الأسعار نيابة عنك',
        'التحقق من الجودة قبل الشراء'
      ]
    },
    { 
      icon: Truck, 
      titleKey: 'services.service2Title', 
      descKey: 'services.service2Desc',
      details: [
        'End-to-end shipping management',
        'Customs clearance assistance',
        'Multiple shipping options',
        'Real-time tracking updates'
      ],
      detailsAr: [
        'إدارة الشحن من البداية إلى النهاية',
        'المساعدة في التخليص الجمركي',
        'خيارات شحن متعددة',
        'تحديثات التتبع في الوقت الفعلي'
      ]
    },
    { 
      icon: ShieldCheck, 
      titleKey: 'services.service3Title', 
      descKey: 'services.service3Desc',
      details: [
        'Pre-shipment quality inspection',
        'Product certification verification',
        'Compliance with international standards',
        'Detailed quality reports'
      ],
      detailsAr: [
        'فحص الجودة قبل الشحن',
        'التحقق من شهادة المنتج',
        'الامتثال للمعايير الدولية',
        'تقارير جودة مفصلة'
      ]
    },
    { 
      icon: FileText, 
      titleKey: 'services.service4Title', 
      descKey: 'services.service4Desc',
      details: [
        'Complete export documentation',
        'Certificate of origin processing',
        'Invoice and packing list preparation',
        'Regulatory compliance support'
      ],
      detailsAr: [
        'وثائق التصدير الكاملة',
        'معالجة شهادة المنشأ',
        'إعداد الفاتورة وقائمة التعبئة',
        'دعم الامتثال التنظيمي'
      ]
    },
  ];

  const additionalServices = [
    { icon: Package, titleKey: 'services.packaging', descKey: 'services.packagingDesc' },
    { icon: Globe, titleKey: 'services.marketResearch', descKey: 'services.marketResearchDesc' },
    { icon: HeadphonesIcon, titleKey: 'services.support', descKey: 'services.supportDesc' },
  ];

  return (
    <div className="min-h-screen pt-16" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-heading mb-6" data-testid="services-title">
              {t('services.title')}
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed" data-testid="services-subtitle">
              {t('services.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-16 md:py-24 bg-white" data-testid="main-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {mainServices.map((service, index) => (
              <Card 
                key={index} 
                className="bg-white border border-slate-100 shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid={`main-service-card-${index}`}
              >
                <CardContent className="p-8">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 bg-primary-blue/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <service.icon className="w-7 h-7 text-primary-blue" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-slate-900 mb-3">
                        {t(service.titleKey)}
                      </h3>
                      <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                        {t(service.descKey)}
                      </p>
                      <ul className="space-y-2">
                        {(isRTL ? service.detailsAr : service.details).map((detail, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                            <div className="w-1.5 h-1.5 bg-accent-red rounded-full flex-shrink-0" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="additional-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-heading mb-4">
              {t('services.additionalTitle')}
            </h2>
            <p className="text-base text-slate-600">
              {t('services.additionalSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {additionalServices.map((service, index) => (
              <Card 
                key={index} 
                className="bg-white border-0 shadow-md hover:shadow-lg transition-all duration-300 text-center"
                data-testid={`additional-service-${index}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary-blue/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <service.icon className="w-6 h-6 text-primary-blue" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {t(service.titleKey)}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {t(service.descKey)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Image Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className={isRTL ? 'order-2' : 'order-1'}>
              <img
                src="https://images.unsplash.com/photo-1766561994067-dbd575e1cff2?auto=format&fit=crop&w=800&q=80"
                alt="Logistics trucks on German highway"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </div>
            <div className={`${isRTL ? 'order-1 text-right' : 'order-2 text-left'}`}>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-heading mb-6">
                {t('services.globalTradeTitle')}
              </h2>
              <p className="text-base text-slate-600 mb-6 leading-relaxed">
                {t('services.globalTradeDesc1')}
              </p>
              <p className="text-base text-slate-600 mb-8 leading-relaxed">
                {t('services.globalTradeDesc2')}
              </p>
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-primary-blue hover:bg-primary-blue/90 text-white"
                  data-testid="services-cta-btn"
                >
                  {t('hero.cta')}
                  <ArrowRight className={`w-5 h-5 ${isRTL ? 'me-2 rotate-180' : 'ms-2'}`} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
